import type { SxProps, Theme } from '@mui/material';

import { caloTheme } from '../../assets/themes/calo';
import { focusRing } from '../chefStyles';

const { palette } = caloTheme;

export const radius = { card: '16px', control: '10px', chip: '6px' } as const;

export const shadow = {
  card: '0 1px 2px rgba(21, 44, 32, 0.04), 0 4px 16px rgba(21, 44, 32, 0.04)',
  raised: '0 2px 4px rgba(21, 44, 32, 0.06), 0 12px 32px rgba(21, 44, 32, 0.08)'
} as const;

export const ease = 'cubic-bezier(0.2, 0, 0, 1)';

export const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** Roboto Mono is not bundled, so the stack falls back to the platform's monospace. */
export const MONO_FONT = "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const mobileOnly = caloTheme.breakpoints.down('sm');
export const fromTablet = caloTheme.breakpoints.up('sm');
export const tabletDown = caloTheme.breakpoints.down('lg');

export const numeric = { fontVariantNumeric: 'tabular-nums' } as const;

export const cardSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  bgcolor: 'white',
  border: '1px solid',
  borderColor: 'neutral100',
  borderRadius: radius.card,
  boxShadow: shadow.card
};

export const cardTitleSx = { fontSize: 16, lineHeight: '24px', fontWeight: 600, color: 'neutral900' } as const;
export const captionSx = { fontSize: 13, lineHeight: '18px', color: 'neutral800' } as const;

export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

export const tones = {
  green: { bg: palette.primary100, ink: palette.primary800 },
  blue: { bg: palette.secondaryBlue100, ink: palette.secondaryBlue800 },
  purple: { bg: palette.secondaryPurple100, ink: palette.secondaryPurple800 },
  yellow: { bg: palette.secondaryYellow100, ink: palette.secondaryYellow800 },
  orange: { bg: palette.secondaryOrange100, ink: palette.secondaryOrange800 },
  red: { bg: palette.secondaryRed50, ink: palette.secondaryRed600 }
} as const;

export type Tone = keyof typeof tones;

const AVATAR_TONES: Tone[] = ['green', 'blue', 'purple', 'yellow', 'orange'];

/** A stable colour per account, so two accounts with one label read apart. */
export const avatarTone = (accountId: string): Tone => {
  const hash = [...accountId].reduce((sum, char) => (sum * 31 + (char.codePointAt(0) ?? 0)) >>> 0, 0);
  return AVATAR_TONES[hash % AVATAR_TONES.length] ?? 'green';
};

const buttonBase = {
  height: 40,
  px: 2,
  borderRadius: radius.control,
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 500,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  '&.Mui-focusVisible, &:focus-visible': focusRing,
  [mobileOnly]: { minHeight: 44 }
} as const;

export const outlineButtonSx: SxProps<Theme> = {
  ...buttonBase,
  bgcolor: 'white',
  borderColor: 'neutral200',
  color: 'primary700',
  '&:hover': { borderColor: 'neutral300', bgcolor: 'primary50' },
  '&.Mui-disabled': { bgcolor: 'neutral50', borderColor: 'neutral200', color: 'neutral800' }
};

/** White on `primary700` clears 4.5:1; the brand green does not. */
export const solidButtonSx: SxProps<Theme> = {
  ...buttonBase,
  bgcolor: 'primary700',
  color: 'white',
  '&:hover': { bgcolor: 'primary800', boxShadow: 'none' }
};
